import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonSearchbar, LoadingController, ModalController, ToastController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { BaseClass } from 'src/app/shared/classes/base';
import { ExternalEvaluationType } from '../external-evaluation/_interfaces/external-evaluation';
import { ExternalUserConfig } from '../external-evaluation/_interfaces/external-user-config';
import { ProcessConfig } from '../shared/interfaces/process-config.interface';
import { ProcessStatusEnum } from '../shared/interfaces/process.interface';
import { ExternalUserCreationComponent } from '../shared/modals/external-user-creation/external-user-creation.component';
import { AuthService } from '../shared/services/auth.service';
import { TranslationService } from '../shared/services/translation.service';
import { UtilService } from '../shared/services/util.service';
import { EvaluatedUser } from './_interfaces/evaluated-user.interface';
import { FreeEvaluationStatus, MidYearEvaluation } from './_interfaces/mid-year-evaluation.interface';
import { FreeEvaluationService } from './free-evaluation-mid-year.service';
import { forEach, isEmpty, isNil } from 'lodash';
import { FreeEvaluationType } from '../shared/components/free-evaluation-users-list/interfaces/free-evaluation-type';
import { EndYearEvaluation } from 'src/app/free-evaluation-end-year/_interfaces/end-year-evaluation.interface';
import { StepsItem } from 'src/app/free-evaluation-mid-year/_interfaces/steps-item.interface';
import { TextAlertModal } from 'src/app/shared/modals/text-alert/text-alert-modal.component';
import { ButtonOptions } from 'src/app/shared/modals/text-alert/interfaces/text-alert.interface';
import { UserRoleEnum } from '../shared/enums/UserRoleEnum';

@Component({
    selector: 'app-free-evaluation-mid-year',
    templateUrl: './free-evaluation-mid-year.page.html',
    styleUrls: ['./free-evaluation-mid-year.page.scss']
})
export class FreeEvaluationMidYearPage extends BaseClass implements OnDestroy {

    @ViewChild('userSearch', { static: false }) userSearch: IonSearchbar;

    private readonly _cycleId: number;
    private readonly _processId: number;
    private _cycleParticipantId: number;
    private _searchSubject: Subject<string> = new Subject();

    public status: ProcessStatusEnum;
    public processConfig: ProcessConfig;
    public FreeEvaluationStatus = FreeEvaluationStatus;

    public users: Array<EvaluatedUser> = [];
    public evaluations: Array<MidYearEvaluation> = [];
    public selectedEvaluations: Array<MidYearEvaluation> = [];

    public canReopenOrDeleteEvaluation: boolean = false;
    public canAddEvaluation: boolean = false;
    public originalEndDate: string = "";
    public originalStartDate: string = "";
    public originalReopeningStepEndDate: string = "";
    public steps: StepsItem = null;

    public processWillOpen: boolean = false;
    public processIsClosed: boolean = false;
    public varIsAvailable: boolean = false;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            public alertController: AlertController,
            private _alertController: AlertController,
            private _loadingController: LoadingController,
            private _router: Router,
            private _activatedRoute: ActivatedRoute,
            private _authService: AuthService,
            private _modalController: ModalController,
            private _evaluationService: FreeEvaluationService,
            private _utilService: UtilService
    ) {
        super(_toastController, _translationService);

        this._processId = this._router.getCurrentNavigation().extras.state
                ? parseInt(this._router.getCurrentNavigation().extras.state.processId)
                : null;
        this._utilService.insertProcessIdInLocalStorage(this._processId);

        this._cycleId = parseInt(
                this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._setSearchSubscription();
        this._setDateSubscription();
    }


    async ionViewWillEnter() {
        const userRoles = this._authService.getUserRoles();
        await this._loadEvaluations(this._cycleId);
        const user = this._authService.getLoggedUser();
        this.canReopenOrDeleteEvaluation = user.roles.includes(UserRoleEnum.Administrator) || userRoles.includes(UserRoleEnum.HR);
    }

    ionViewDidLeave() {
        this._utilService.removeProcessIdFromLocalStorage();
        this.selectedEvaluations = [];
    }

    public async goBack(returnToDashboard: boolean = false): Promise<void> {
        if (!returnToDashboard
                && this.varIsAvailable
                && this.evaluations
                && this.evaluations.some(freeEval => freeEval.status === FreeEvaluationStatus.NotSent)) {
            this._openFreeEvaluationsToSendModal().finally();
        } else {
            this._utilService.removeProcessIdFromLocalStorage();
            await this._router.navigate(['dashboard']);
        }
    }

    public isAvailable(processConfig: ProcessConfig | null): boolean {
        if (processConfig) {

            if (!processConfig.startDate || !processConfig.endDate || processConfig.closed)
                return false;

            const today = new Date(Date.now());
            const startDate = new Date(this.originalStartDate);
            const endDate = new Date(this.originalEndDate);

            const reopeningStepEndDate = new Date(this.steps.stepEndAfterReopening)

            let reopeningStepEndActivated = today > endDate && today <= reopeningStepEndDate;
            this.processIsClosed = today >= endDate && !reopeningStepEndActivated;
            this.processWillOpen = today < startDate && !reopeningStepEndActivated;
            this.varIsAvailable = (today >= startDate && today <= endDate) || reopeningStepEndActivated;
            return (today >= startDate && today <= endDate) || reopeningStepEndActivated;
        }
        return false;
    }

    public async setUser(user: EvaluatedUser, config: ProcessConfig): Promise<void> {
      const userFlags = user.flags;
        const errorMessages: string[] = [];

        const errorTranslations = {
            'Cycles.UserNotInCycle': 'Cycles.UserNotInCycle',
        };

        for (const flag of userFlags) {
            if (errorTranslations[flag]) {
                let errorMessage = this._translationService.getTranslation(errorTranslations[flag]);
               
                errorMessages.push(errorMessage);
            }
        }

        if (errorMessages.length > 0) {
            const combinedErrorMessage = errorMessages.join('\n');
            this.notify(combinedErrorMessage, 'danger');
            return;
        }

    if (this.status === ProcessStatusEnum.AllowedViewing && this.isAvailable(this.processConfig)) {
        let leftOption: ButtonOptions = {
            text: 'generic.leaveThisPage',
            signal: true
        };

        let rightOption: ButtonOptions = {
            text: 'freeEvaluations.reopenStep',
            signal: false
        };

        const modal = await this._modalController.create({
            component: TextAlertModal,
            cssClass: 'reopen-free-evaluation-step',
            componentProps: {
                'evaluationType': FreeEvaluationType.MidYear,
                'closeButtonHeader': true,
                'headerText': 'freeEvaluations.reopeningEvaluationStep',
                'contentText': 'freeEvaluations.confirmReopenMidYearEvaluation',
                'leftOption': leftOption,
                'rightOption': rightOption
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data === false) {
                this._evaluationService.reopenUserStepEvaluation(
                    this.processConfig.type, this._cycleParticipantId, this.processConfig.id, false
                ).subscribe((response) => {
                    this.goToNewEvaluation(user, config);
                    this.users = [];
                    this.userSearch.value = '';
                }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
            } else {
                return;
            }
        });

        return await modal.present();
    } else {
        this.goToNewEvaluation(user, config);
        this.users = [];
        this.userSearch.value = '';
    }
}


    public goToNewEvaluation(user: EvaluatedUser, processConfig: ProcessConfig): void {
        this._setUserInStorage(user);
        this._setQuestionsInStorage(processConfig);
        this._router.navigate([
            'free-evaluation-mid-year/' + this._cycleId +
            '/' + this._cycleParticipantId +
            '/' + user.subjectUserId
        ]);
    }

    public editEvaluation(eventData: { evaluation: MidYearEvaluation | EndYearEvaluation, processConfig: ProcessConfig }): void {
        const { evaluation, processConfig } = eventData;

        this._setQuestionsInStorage(processConfig);
        this._setEvaluationInStorage(evaluation);
        this._router.navigate([
            'free-evaluation-mid-year/' + this._cycleId +
            '/' + this._cycleParticipantId +
            '/' + evaluation.subjectUserId
        ]);
    }

    private _setUserInStorage(user: EvaluatedUser): void {
        localStorage.setItem(
                'FreeEvaluationMidYear_EvaluatedUser',
                JSON.stringify(user)
        );
    }

    private _setQuestionsInStorage(processConfig: ProcessConfig): void {
        localStorage.setItem(
                'FreeEvaluationMidYear_ProcessConfig',
                JSON.stringify(processConfig)
        );
    }

    private _setEvaluationInStorage(evaluation: MidYearEvaluation): void {
        localStorage.setItem(
                'FreeEvaluationMidYear_Evaluation',
                JSON.stringify(evaluation)
        );
    }

    public updateSearch(searchTextValue: string): void {
        this._searchSubject.next(searchTextValue);
    }

    public async confirmSendEvaluations(evaluations: Array<MidYearEvaluation>): Promise<void> {
        let endDate = null;
        if (this.processConfig.endDate) {
            let formattedEndDate = new Date(this.originalEndDate);
            endDate = formattedEndDate ? formattedEndDate : null;
        }
        const todayConfig = new Date(Date.now());

        if (this.steps.stepEndAfterReopening && endDate <= todayConfig) {
            const today = new Date(Date.now());
            const reopeningStepEndDate = new Date(this.steps.stepEndAfterReopening);
            if (reopeningStepEndDate < today) {
                await this.notify(
                        this._translationService.getTranslation(
                                'freeEvaluationMidYear.evaluationHasClosed'
                        ), 'danger'
                );
                return;
            }
        }

        if (this.status === ProcessStatusEnum.AllowedViewing) {
            await this.notify(
                    this._translationService.getTranslation(
                            'freeEvaluation.stepFinished'
                    ), 'danger'
            );
            return;
        }
        if (evaluations.length > this.processConfig.maximumEvaluationQuantity) {
            await this.notify(
                    this._translationService.getTranslation(
                            'freeEvaluationMidYear.maxEvaluationLimit'
                    ), 'danger'
            );
            return;
        }

        if (!this._validateEvaluations(evaluations)) {
            await this.notify(
                    this._translationService.getTranslation(
                        'freeEvaluationMidYear.duplicateEvaluations'
                    ), 'danger'
            );
            return;
        }

        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('freeEvaluationMidYear.confirmSendEvaluations'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._sendEvaluations(evaluations);
                }
            }]
        });

        await alert.present();
    }

    public async confirmSendEvaluation(eventData: { evaluation: MidYearEvaluation | EndYearEvaluation }): Promise<void> {
        const { evaluation } = eventData;

        let endDate = null;
        if (this.processConfig.endDate) {
            let formattedEndDate = new Date(this.originalEndDate);
            endDate = formattedEndDate ? formattedEndDate : null;
        }
        const todayConfig = new Date(Date.now());

        if (this.steps.stepEndAfterReopening && endDate <= todayConfig) {
            const today = new Date(Date.now());
            const reopeningStepEndDate = new Date(this.steps.stepEndAfterReopening);
            if (reopeningStepEndDate < today) {
                await this.notify(
                        this._translationService.getTranslation(
                                'freeEvaluationMidYear.evaluationHasClosed'
                        ), 'danger'
                );
                return;
            }
        }

        if (this.status === ProcessStatusEnum.AllowedViewing) {
            await this.notify(
                    this._translationService.getTranslation(
                            'freeEvaluation.stepFinished'
                    ), 'danger'
            );
            return;
        }
        let evalQuantity = (this.evaluations.filter(ev => ev.status === FreeEvaluationStatus.Sent).length + 1);
        if (evalQuantity > this.processConfig.maximumEvaluationQuantity) {
            await this.notify(
                    this._translationService.getTranslation(
                            'freeEvaluationMidYear.maxEvaluationLimit'
                    ), 'danger'
            );
            return;
        }

        if (!this._validateEvaluation(evaluation)) {
            await this.notify(
                    this._translationService.getTranslation(
                            'freeEvaluationMidYear.duplicateEvaluations'
                    ), 'danger'
            );
            return;
        }

        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('freeEvaluationMidYear.confirmSendEvaluation'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    let evaluationArray: Array<MidYearEvaluation | EndYearEvaluation> = [];
                    evaluationArray.push({ ...evaluation });
                    this._sendEvaluations(evaluationArray, false);
                }
            }]
        });

        await alert.present();
    }

    private _validateEvaluations(evaluations: Array<MidYearEvaluation>): boolean {
        const userIds: Array<string> = evaluations.map(ev => ev.subjectUserId);
        return userIds.every(uId => {
            const idCount = userIds.filter(id => id === uId).length;
            return idCount === 1;
        });
    }

    private _validateEvaluation(evaluation: MidYearEvaluation | EndYearEvaluation): boolean {
        const userIds: Array<string> = this.evaluations
                .filter(ev => ev.status === FreeEvaluationStatus.Sent)
                .map(ev => ev.subjectUserId);
        return userIds.every(uId => {
            const idCount = userIds.filter(id => uId === id).length;
            const evalNotSent = userIds.filter(id => id === evaluation.subjectUserId).length === 0;
            return idCount === 1 && evalNotSent;
        });
    }

    private _sendEvaluations(evaluations: Array<MidYearEvaluation>, returnDashboard = true): void {
        if (evaluations.length > 0) {
            const evaluationsIds = evaluations.map(ev => ev.id);
            const actualDate = new Date();
            const offset = actualDate.getTimezoneOffset();
            const localDateMilliseconds = actualDate.getTime() - (offset * 60 * 1000);
            const localDate = new Date(localDateMilliseconds);
            const isoStringLocal = localDate.toISOString();

            const reopeningStepEndDateActualDate = new Date(this.steps.stepEndAfterReopening);
            const reopeningStepEndDateOffset = reopeningStepEndDateActualDate.getTimezoneOffset();
            const reopeningStepEndDateLocalDateMilliseconds = reopeningStepEndDateActualDate.getTime() - (reopeningStepEndDateOffset * 60 * 1000);
            const reopeningStepEndDateLocalDate = new Date(reopeningStepEndDateLocalDateMilliseconds);
            const reopeningStepEndDateIsoStringLocal = reopeningStepEndDateLocalDate.toISOString();

            this._evaluationService.sendMidYearEvaluations(this._cycleParticipantId, evaluationsIds, isoStringLocal, reopeningStepEndDateIsoStringLocal)
                    .subscribe(async () => {
                        await this.notify(
                                this._translationService.getTranslation(
                                        'generic.saveSuccess'
                                ), 'success'
                        );
                        if (returnDashboard)
                            await this.goBack(returnDashboard);
                        else
                            await this._loadEvaluations(this._cycleId);

                    }, (error) =>
                            this.notify(this.getErrorNotification(error), 'danger'));
        }
    }

    public async confirmDeleteEvaluation(eventData: { evaluation: MidYearEvaluation | EndYearEvaluation }): Promise<void> {
        const { evaluation } = eventData;

        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('freeEvaluationMidYear.confirmDeleteEvaluation'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._deleteEvaluation(evaluation.id);
                }
            }]
        });

        await alert.present();
    }

    private async _deleteEvaluation(evaluationId: number): Promise<void> {
        this._evaluationService.deleteMidYearEvaluation(
            evaluationId
        ).subscribe(() => {
            this._loadEvaluations(this._cycleId);
            this.notify(
                this._translationService.getTranslation(
                    'generic.deleteSuccess'
                ), 'success'
            );

        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    private async _loadEvaluations(cycleId: number): Promise<void> {
        // const loading = await this._loadingController.create();
        // await loading.present();

        this._evaluationService.getMidYearEvaluations(
                cycleId
        ).subscribe((response) => {
            this.steps = response.data.steps;
            this.status = this.steps.status;
            this.evaluations = response.data.evaluations;
            this.evaluations = forEach(this.evaluations, (ev) => {
                ev.createdAt = this._utilService.convertDateString(ev.createdAt, true)
            });
            this.processConfig = response.data.config;
            this._cycleParticipantId = response.data.cycleParticipantId;
            this.selectedEvaluations = [];

            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            this.originalStartDate = this.processConfig.startDate;
            this.originalEndDate = this.processConfig.endDate;
            this._updateStartAndEndDate();

            this.originalReopeningStepEndDate = this.steps
                    ? (this.steps.stepEndAfterReopening
                            ? new Date(this.steps.stepEndAfterReopening)
                                    .toLocaleString('default', { timeZone: userTimeZone })
                            : null)
                    : null;

            this.canAddEvaluation = this._canAddNewFreeEvaluation();
            this.isAvailable(this.processConfig);
            // loading.dismiss();

        }, (error) => {
            // loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private async _loadUsers(cycleId: number, searchString: string): Promise<void> {
        this._evaluationService.getUsersForEvaluation(
                cycleId, searchString
        ).subscribe((response) => {
            this.users = response.data;
        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(
                debounceTime(500),
                distinctUntilChanged()
        ).subscribe((searchValue: string) => {
            this.users = [];
            if (searchValue) {
                this._loadUsers(this._cycleId, searchValue);
            }
        });
    }

    private _canAddNewFreeEvaluation(): boolean {
        if (!this.processConfig || !this.processConfig.draftStartDate)
            return false;

        const today = new Date(Date.now());
        let startDate = null;
        if (this.processConfig.startDate) {
            let formattedStartDate = new Date(this.originalStartDate);
            startDate = formattedStartDate ? formattedStartDate : null;
        }

        let endDate = null;
        if (this.processConfig.endDate) {
            let formattedEndDate = new Date(this.originalEndDate);
            endDate = formattedEndDate ? formattedEndDate : null;
        }

        const reopeningStepEndDate = new Date(this.steps.stepEndAfterReopening);
        let reopeningStepEndActivated = today > endDate && today <= reopeningStepEndDate;

        return (startDate <= today && (!this.processConfig.endDate || endDate >= today))
                || reopeningStepEndActivated;
    }

    public async reopenEvaluation(eventData: { evaluation: MidYearEvaluation | EndYearEvaluation }) {
        const { evaluation } = eventData;

        const alert = await this._alertController.create({
            message: this._translationService.getTranslation('managerEvaluation.confirmReopen'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => this._handleOpenEvaluation(evaluation)
            }]
        });

        await alert.present();
    }

    public async requestExternalEvaluation() {
        const user = this._authService.getImpersonatedUser();
        const externalUser: ExternalUserConfig = {
            cycleId: this._cycleId,
            evaluatedUserId: user.userId,
            evaluatedUserImageUrl: user.imageUrl,
            evaluatedUserName: user.username,
            cycleName: '',
            type: ExternalEvaluationType.FreeEvaluationMidYear
        };
        const modal = await this._modalController.create({
            component: ExternalUserCreationComponent,
            cssClass: 'bigger-modal',
            componentProps: {
                externalUser: externalUser,
                isAdmin: false
            },
        });
        await modal.present();
    }

    private async _handleOpenEvaluation(evaluation: MidYearEvaluation) {
        // const loading = await this._loadingController.create();
        // await loading.present();
        this._evaluationService.reopenMidYearEvaluation(
                this._cycleId,
                evaluation.subjectUserId
        ).subscribe(() => {
            this.ionViewWillEnter();
            // loading.dismiss();

        }, (error) => {
            // loading.dismiss();
            this.notifyError(error);
        });
    }

    private _setDateSubscription() {
        this._translationService.languageChanged$.pipe(
                tap(() => {
                    this._updateStartAndEndDate();
                })
        ).subscribe();
    }

    private _updateStartAndEndDate() {
        if (this.processConfig) {
            this.processConfig.startDate = !isNil(this.originalStartDate) && !isEmpty(this.originalStartDate) ? this._utilService.convertDateString(this.originalStartDate) : this.processConfig.startDate;
            this.processConfig.endDate = !isNil(this.originalEndDate) && !isEmpty(this.originalEndDate) ? this._utilService.convertDateString(this.originalEndDate) : this.processConfig.endDate;
        }
    }

    private async _openFreeEvaluationsToSendModal() {
        let leftOption: ButtonOptions = {
            text: 'generic.stayOnThisPage',
            signal: false
        }

        let rightOption: ButtonOptions = {
            text: 'generic.leaveThisPage',
            signal: true
        }
        const modal = await this._modalController.create({
            component: TextAlertModal,
            cssClass: 'free-eval-to-send',
            componentProps: {
                'evaluationType': FreeEvaluationType.MidYear,
                'closeButtonHeader': true,
                'headerText': 'generic.attention',
                'contentText': 'freeEvaluation.freeEvaluationsToSend',
                'leftOption': leftOption,
                'rightOption': rightOption
            }
        });

        modal.onDidDismiss().then((dismissEvent) => {
            if (dismissEvent.data === true) {
                this._utilService.removeProcessIdFromLocalStorage();
                this._router.navigate(['dashboard']);
            }
            return;
        });
        return await modal.present();
    }

    ngOnDestroy() {
        this._searchSubject.unsubscribe();
    }

    public readonly FreeEvaluationType = FreeEvaluationType;
}
